import React, { useState } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  AppBar,
  Toolbar,
  Card,
  CardContent,
  Button,
  LinearProgress,
  Alert,
  Chip,
  Grid,
  Paper,
  Divider,
  IconButton,
  Snackbar
} from '@mui/material';
import {
  Upload as UploadIcon,
  ShoppingCart as ShoppingCartIcon,
  Assessment as AssessmentIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Menu as MenuIcon,
  Close as CloseIcon
} from '@mui/icons-material';

// Stok Raporları componenti import et
import StokRaporlari from './StokRaporlari';

const UploadProduct = () => {
  const [selectedPage, setSelectedPage] = useState('alis');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  let status = "0";
  let path = status === "0" ? "http://localhost:3003" : "https://outlettee.com:3003";

  const drawerWidth = 280;

  const menuItems = [
    {
      id: 'alis',
      text: 'Alış Exceli Yükle',
      icon: <UploadIcon />,
      color: '#2196f3'
    },
    {
      id: 'satis',
      text: 'Satış Exceli Yükle',
      icon: <ShoppingCartIcon />,
      color: '#4caf50'
    },
    {
      id: 'raporlar',
      text: 'Raporlar',
      icon: <AssessmentIcon />,
      color: '#ff9800'
    }
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleFileUpload = async (file, type) => {
    if (!file) return;

    const formData = new FormData();
    const fieldName = type === 'alis' ? 'alisFile' : 'satisFile';
    formData.append(fieldName, file);

    setIsUploading(true);
    setUploadProgress(0);
    setUploadResult(null);

    try {
      // Progress simulation
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + Math.random() * 15;
        });
      }, 200);

      const response = await fetch(`${path}/upload-${type}`, {
        method: 'POST',
        body: formData
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      const result = await response.json();

      setTimeout(() => {
        setUploadResult(result);
        setIsUploading(false);
        setUploadProgress(0);
        
        if (result.success) {
          setSnackbar({
            open: true,
            message: 'Dosya başarıyla yüklendi!',
            severity: 'success'
          });
        } else {
          setSnackbar({
            open: true,
            message: result.message || 'Yükleme başarısız!',
            severity: 'error'
          });
        }
      }, 500);

    } catch (error) {
      setIsUploading(false);
      setUploadProgress(0);
      setUploadResult({
        success: false,
        message: 'Bağlantı hatası: ' + error.message
      });
      setSnackbar({
        open: true,
        message: 'Bağlantı hatası oluştu!',
        severity: 'error'
      });
    }
  };

  const FileUploadCard = ({ type, title, description, acceptedFormats, icon, color }) => {
    const [dragActive, setDragActive] = useState(false);
    const inputRef = React.useRef(null);

    const handleDrag = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.type === "dragenter" || e.type === "dragover") {
        setDragActive(true);
      } else if (e.type === "dragleave") {
        setDragActive(false);
      }
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        handleFileUpload(e.dataTransfer.files[0], type);
      }
    };

    const handleFileSelect = (e) => {
      if (e.target.files && e.target.files[0]) {
        handleFileUpload(e.target.files[0], type);
      }
    };

    return (
      <Card 
        sx={{ 
          height: '100%',
          border: dragActive ? `2px dashed ${color}` : '2px dashed #e0e0e0',
          backgroundColor: dragActive ? `${color}08` : 'white',
          transition: 'all 0.3s ease'
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <CardContent sx={{ textAlign: 'center', p: 4 }}>
          <Box sx={{ mb: 3 }}>
            {React.cloneElement(icon, { 
              sx: { fontSize: 64, color: color, mb: 2 } 
            })}
            <Typography variant="h5" component="h2" gutterBottom sx={{ color: color }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {description}
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              {acceptedFormats.map((format, index) => (
                <Chip 
                  key={index}
                  label={format} 
                  size="small" 
                  sx={{ mr: 1, mb: 1 }}
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>

          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />

          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={() => inputRef.current?.click()}
            disabled={isUploading}
            sx={{ 
              backgroundColor: color,
              '&:hover': { backgroundColor: color + 'dd' },
              mb: 2 
            }}
            size="large"
          >
            {isUploading ? 'Yükleniyor...' : 'Dosya Seç'}
          </Button>

          <Typography variant="caption" display="block" color="text.secondary">
            veya dosyayı buraya sürükleyin
          </Typography>

          {isUploading && (
            <Box sx={{ mt: 3 }}>
              <LinearProgress 
                variant="determinate" 
                value={uploadProgress} 
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                %{Math.round(uploadProgress)} tamamlandı
              </Typography>
            </Box>
          )}

          {uploadResult && (
            <Box sx={{ mt: 3 }}>
              <Alert 
                severity={uploadResult.success ? 'success' : 'error'}
                icon={uploadResult.success ? <CheckCircleIcon /> : <ErrorIcon />}
              >
                <Typography variant="body2">
                  {uploadResult.message}
                </Typography>
                {uploadResult.stats && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" display="block">
                      İşlenen: {uploadResult.stats.totalProcessed} | 
                      Başarılı: {uploadResult.stats.successCount} | 
                      Hatalı: {uploadResult.stats.errorCount}
                    </Typography>
                  </Box>
                )}
              </Alert>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderContent = () => {
    switch (selectedPage) {
      case 'alis':
        return (
          <Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 4 }}>
              Alış Excel Dosyası Yükleme
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FileUploadCard
                  type="alis"
                  title="Alış Verileri"
                  description="Alış Excel dosyanızı yükleyerek stok giriş bilgilerinizi sisteme aktarın"
                  acceptedFormats={['.xlsx', '.xls']}
                  icon={<UploadIcon />}
                  color="#2196f3"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    📋 Gerekli Sütunlar
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        • <strong>VENDOR:</strong> Tedarikçi adı
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        • <strong>TARİH:</strong> Alış tarihi
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        • <strong>ÜRÜN SKU:</strong> Ürün kodu
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        • <strong>COLOR:</strong> Ürün rengi
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        • <strong>SİZE:</strong> Ürün bedeni
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        • <strong>QTY:</strong> Miktar
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        • <strong>Price:</strong> Birim fiyat
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        • <strong>BİRLEŞİK İSİM:</strong> Ürün kodu (örn: 3001-S-Black)
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        );

      case 'satis':
        return (
          <Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 4 }}>
              Satış Excel Dosyası Yükleme
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FileUploadCard
                  type="satis"
                  title="Satış Verileri"
                  description="Satış Excel dosyanızı yükleyerek stok çıkış bilgilerinizi sisteme aktarın"
                  acceptedFormats={['.xlsx', '.xls']}
                  icon={<ShoppingCartIcon />}
                  color="#4caf50"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    📋 Gerekli Sütunlar
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        • <strong>Sipariş Numarası:</strong> Benzersiz sipariş kodu
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        • <strong>Mağaza:</strong> Satış kanalı
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        • <strong>Sipariş Tarihi:</strong> Satış tarihi
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        • <strong>Stok Kodu:</strong> Ürün stok kodu
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        • <strong>Miktar:</strong> Satış miktarı
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        • <strong>KOD:</strong> Ürün kodu (örn: 3001Y-S-True Royal)
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        );

      case 'raporlar':
        return (
          <Box>
            <StokRaporlari path={path} />
          </Box>
        );

      default:
        return null;
    }
  };

  const drawer = (
    <Box>
      <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
          🏪 Stok Takip Sistemi
        </Typography>
      </Box>
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.id} disablePadding>
            <ListItemButton
              selected={selectedPage === item.id}
              onClick={() => {
                setSelectedPage(item.id);
                setMobileOpen(false);
              }}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: `${item.color}15`,
                  borderRight: `3px solid ${item.color}`,
                  '& .MuiListItemIcon-root': {
                    color: item.color,
                  },
                  '& .MuiListItemText-primary': {
                    color: item.color,
                    fontWeight: 'bold',
                  },
                },
                '&:hover': {
                  backgroundColor: `${item.color}08`,
                }
              }}
            >
              <ListItemIcon>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* AppBar for mobile */}
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          display: { sm: 'none' },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div">
            Stok Takip Sistemi
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: { xs: 8, sm: 0 },
        }}
      >
        {renderContent()}
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UploadProduct;